import React from "react"
import { useNavigate } from "react-router-dom"
import {
	Text,
	Flex,
	Button,
	TextField,
	Checkbox,
	FilterCheckbox,
	Icon,
	useModal,
} from "@capioweb/ui-components/src"
import {
	CapioSharedLoginFormStyled,
	CapioSharedLoginFormFooterStyled,
} from "../CapioLoginPage/styled"
import { LocalesContext } from "../../context"
import { SharedFormProps } from "../../@types/CapioWeb/core"
import { RegistrationFormContext } from "./context/RegistrationFormContext"
import { InfoModal } from "./InfoModal"
import { postAccountForm } from "../../components/MyPages/api/postAccountForm"
import { useForm } from "react-hook-form"
import { getAntiforgeryToken } from '../../utils/antiforgery'
import { StyledButtonContent, StyledIcon } from '../../components/MyPagesUserLifestyleFormBlock/LifestyleForm'

export interface RegistrationFormProps {
	forms: {
		registrationForm: SharedFormProps
		interestsForm?: SharedFormProps
	}
}

const RegistrationForm: React.FC<RegistrationFormProps> = (props) => {
	const [loading, setLoading] = React.useState<boolean>(false)

	// Set current form variant and get active form element
	const { activeFormVariant, setActiveFormVariant } = React.useContext(
		RegistrationFormContext
	)
	// Holds props for currently active form
	const [activeForm, setActiveForm] = React.useState(
		props.forms[activeFormVariant]
	)
	// State for feedback message on form error/success
	const [feedbackMsg, setFeedbackMsg] =
		React.useState<{
			success: boolean
			message: string
		}>(null)
	// Register form
	const {
		reset,
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		mode: "onChange",
	})

	const navigate = useNavigate()
	const { translate, translationKeys } = React.useContext(LocalesContext)

	React.useEffect(() => {
		// Reset hook forms registrations
		reset()

		// Update active form element when form variant changes
		setActiveForm(props.forms[activeFormVariant])
	}, [activeFormVariant])

	// Submit handler
	const onSubmit = async (data) => {
		setFeedbackMsg(null)
		setLoading(true)


		const { action } = activeForm
		var antiForgeryToken = await getAntiforgeryToken()
		try {
			const result = await postAccountForm(
				action,
				antiForgeryToken,
				data,
				"POST"
			)

			if (result.success) {
				// If we're on step 1, go to next
				// Else finish submit and go to redirect
				if (
					props.forms.interestsForm &&
					activeFormVariant === "registrationForm"
				) {
					setActiveFormVariant("interestsForm")
				} else {
					navigate(activeForm.successLink ?? "/")
				}
			} else {
				setFeedbackMsg({
					success: false,
					message: result.data?.message ?? result.statusCode,
				})
			}
		} catch (e) {
			console.error("---Catch", e, "--- error")
		}
		setLoading(false)
	}

	const { addModal } = useModal()

	return (
		<CapioSharedLoginFormStyled
			action={activeForm.action}
			encType="application/x-www-form-urlencoded"
			method={activeForm.method}
			onSubmit={handleSubmit(onSubmit)}
		>
			<Flex
				w={1}
				flexDirection="column"
				pt={{ _: 3, m: 5 }}
				pb={2}
				px={{ _: 4, m: 9 }}
			>
				{activeForm.heading && (
					<Text variant="headline-1" mb={3}>
						{activeForm.heading}
					</Text>
				)}
				{activeForm.description && <Text>{activeForm.description}</Text>}

				{activeForm.fields.map((field) => {
					const isInputField = !["rte", "captcha", "submit"].includes(
						field.type
					)

					if (field && field.type && isInputField) {
						const { type, value, required, ...rest } = field
						// https://react-hook-form.com/api/useform/register
						const { ref, ...registerProps } = register(field.name, {
							required: {
								value:
									field.type !== "checkbox" || field.name === "terms"
										? true
										: false,
								message: activeForm.requiredMessage,
							},
						})
						const fieldProps = {
							...registerProps,
							...rest,
							type,
							key: field.id,
							error: errors[field.name],
							...(field.type !== "hidden" &&
								errors[field.name] && {
								helperText: errors[field.name].message,
							}),
						}

						if (["text", "email", "tel"].includes(type)) {
							return (
								<TextField
									inputRef={ref}
									defaultValue={value}
									{...fieldProps}
								/>
							)
						} else {
							return {
								checkbox:
									field.items && field.items.length > 1 ? (
										<Flex
											key={field.id}
											as="fieldset"
											flexWrap="wrap"
											rowGap={3}
											columnGap={1.5}
										>
											{field.items.map((item, i) => {
												const { checked, ...restItem } = item

												const { ref, ...rest } = register(field.name)

												return (
													<Flex key={item.value}>
														<FilterCheckbox
															inputRef={ref}
															id={field.name + "-" + item.value}
															name={field.name}
															label={item.caption}
															value={item.value}
															{...restItem}
															{...rest}
														/>
													</Flex>
												)
											})}
										</Flex>
									) : (
										<Flex key={field.id} mb={2} justifyContent="space-between">
											<Checkbox
												inputRef={ref}
												value={field.value ?? field.label}
												defaultChecked={
													field.items?.length > 0 && field.items[0].checked
												}
												{...fieldProps}
											/>
											{activeForm.modals?.find((m) => m.id === field.id) && (
												<Flex
													as="button"
													aria-label={`${translate(
														translationKeys.openModalFor
													)} ${field.label}`}
													flexShrink={0}
													onClick={(e) => {
														e.preventDefault()
														addModal(
															<InfoModal
																lastFocusedElement={e.target}
																{...activeForm.modals.find(
																	(m) => m.id === field.id
																)}
															/>
														)
													}}
												>
													<Icon
														icon="info-circle"
														size={3}
													// style={{ pointerEvents: "none" }}
													/>
												</Flex>
											)}
										</Flex>
									),
								hidden: (
									<input
										ref={ref}
										readOnly
										key={field.value}
										value={field.value}
										{...fieldProps}
									/>
								),
							}[type]
						}
					}
				})}
			</Flex>
			<CapioSharedLoginFormFooterStyled
				w={1}
				flexDirection="column"
				py={3}
				px={{ _: 4, m: 9 }}
			>
				<Flex
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					{/* Abort link */}
					<Text.Anchor
						href={activeForm.abortLink ?? "/"}
						style={{ textDecoration: "underline" }}
					>
						{activeFormVariant === "registrationForm"
							? translate(translationKeys.abort)
							: translate(translationKeys.chooseLater)}
					</Text.Anchor>

					{/* Submit button */}
					<Button ml="auto" style={{ position: 'relative' }} type={loading ? 'button' : "submit"}>
						<StyledIcon aria-hidden loading={loading}>
							<Icon icon="ellipse" color='white' />
						</StyledIcon>
						<StyledButtonContent loading={loading}>

							{activeFormVariant === "registrationForm"
								? translate(translationKeys.next)
								: translate(translationKeys.save)}
						</StyledButtonContent>
					</Button>


				</Flex>

				{/* Feedback message - used if we don't redirect (errors & success) */}
				<Flex
					h={feedbackMsg ? "auto" : "0"}
					overflow="hidden"
					w={1}
					mt={1.5}
					justifyContent="flex-end"
					role="region"
					aria-live="polite"
				>
					<Text
						as="span"
						textColor={feedbackMsg?.success ? "primary" : "alert"}
					>
						{feedbackMsg?.message}
					</Text>
				</Flex>
			</CapioSharedLoginFormFooterStyled>
		</CapioSharedLoginFormStyled>
	)
}

export { RegistrationForm }
